import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api, type LcPoolResponse } from '@/lib/api';
import { InventoryTokenPicker, type SelectedToken } from '../components/InventoryTokenPicker';
import { CacheEditForm } from '../components/CacheEditForm';

export function AdminPage() {
  // Create form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tokenASymbol, setTokenASymbol] = useState('');
  const [tokenAName, setTokenAName] = useState('');
  const [tokenAInventory, setTokenAInventory] = useState<SelectedToken | null>(null);
  const [tokenADecimals, setTokenADecimals] = useState('0');
  const [tokenASource, setTokenASource] = useState<'inventory' | 'external'>('inventory');
  const [tokenBSymbol, setTokenBSymbol] = useState('');
  const [tokenBName, setTokenBName] = useState('');
  const [tokenBInventory, setTokenBInventory] = useState<SelectedToken | null>(null);
  const [tokenBDecimals, setTokenBDecimals] = useState('0');
  const [tokenBSource, setTokenBSource] = useState<'inventory' | 'external'>('inventory');
  const [feeBps, setFeeBps] = useState('30');
  const [minLiquidity, setMinLiquidity] = useState('1000');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Cache list state
  const [pools, setPools] = useState<LcPoolResponse[]>([]);
  const [poolsLoading, setPoolsLoading] = useState(true);
  const [editingPoolId, setEditingPoolId] = useState<string | null>(null);

  const loadPools = useCallback(async () => {
    setPoolsLoading(true);
    try {
      const res = await api.listLiquidityCachePools({ limit: 100 });
      setPools(res.pools);
    } catch {
      // ignore
    } finally {
      setPoolsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPools();
  }, [loadPools]);

  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const pool = await api.createLiquidityCachePool({
        name,
        description: description || undefined,
        tokenA: {
          itemDefId: tokenASource === 'inventory' ? tokenAInventory?.itemDefId : undefined,
          symbol: tokenASymbol,
          name: tokenAName,
          decimals: parseInt(tokenADecimals, 10) || 0,
          source: tokenASource,
        },
        tokenB: {
          itemDefId: tokenBSource === 'inventory' ? tokenBInventory?.itemDefId : undefined,
          symbol: tokenBSymbol,
          name: tokenBName,
          decimals: parseInt(tokenBDecimals, 10) || 0,
          source: tokenBSource,
        },
        feeBps: parseInt(feeBps, 10) || 30,
        minLiquidity: parseInt(minLiquidity, 10) || 1000,
      });
      setSuccess(`Cache created: ${pool.poolId}`);
      setName('');
      setDescription('');
      setTokenASymbol('');
      setTokenAName('');
      setTokenAInventory(null);
      setTokenBSymbol('');
      setTokenBName('');
      setTokenBInventory(null);
      setMinLiquidity('1000');
      loadPools();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create cache');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-gray-100 text-gray-500',
    };
    return (
      <span
        className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[status] ?? 'bg-gray-100'}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold">Cache Administration</h1>

      {/* ── Existing Caches ────────────────────────────────────────────── */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Existing Caches</h2>
        {poolsLoading ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : pools.length === 0 ? (
          <p className="text-muted-foreground text-sm">No caches yet.</p>
        ) : (
          <div className="space-y-3">
            {pools.map((pool) =>
              editingPoolId === pool.poolId ? (
                <CacheEditForm
                  key={pool.poolId}
                  pool={pool}
                  onSaved={() => {
                    setEditingPoolId(null);
                    loadPools();
                  }}
                  onCancel={() => setEditingPoolId(null)}
                />
              ) : (
                <Card key={pool.poolId}>
                  <CardContent className="flex items-center justify-between py-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{pool.name}</span>
                        {statusBadge(pool.status)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {pool.tokenA.name} ({pool.tokenA.symbol}) / {pool.tokenB.name} (
                        {pool.tokenB.symbol}) &middot; Fee: {pool.feeBps} bps &middot; Min
                        Liquidity: {pool.minLiquidity}
                        &middot; Max Trade: {pool.maxTradePct}%
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingPoolId(pool.poolId)}
                    >
                      Edit
                    </Button>
                  </CardContent>
                </Card>
              ),
            )}
          </div>
        )}
      </div>

      {/* ── Create Cache Form ──────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Create New Cache</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Cache Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Cache" />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Cache description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <h3 className="font-medium">Token A</h3>
              <div>
                <Label>Symbol</Label>
                <Input
                  value={tokenASymbol}
                  onChange={(e) => setTokenASymbol(e.target.value)}
                  placeholder="GOLD"
                />
              </div>
              <div>
                <Label>Name</Label>
                <Input
                  value={tokenAName}
                  onChange={(e) => setTokenAName(e.target.value)}
                  placeholder="Gold Coins"
                />
              </div>
              <div>
                <Label>Source</Label>
                <select
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  value={tokenASource}
                  onChange={(e) => {
                    setTokenASource(e.target.value as 'inventory' | 'external');
                    setTokenAInventory(null);
                  }}
                >
                  <option value="inventory">Inventory (DFE)</option>
                  <option value="external">External (Simulated)</option>
                </select>
              </div>
              {tokenASource === 'inventory' && (
                <InventoryTokenPicker
                  label="Inventory Item"
                  selected={tokenAInventory}
                  onSelect={setTokenAInventory}
                />
              )}
              <div>
                <Label>Decimals</Label>
                <Input value={tokenADecimals} onChange={(e) => setTokenADecimals(e.target.value)} />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-medium">Token B</h3>
              <div>
                <Label>Symbol</Label>
                <Input
                  value={tokenBSymbol}
                  onChange={(e) => setTokenBSymbol(e.target.value)}
                  placeholder="GEM"
                />
              </div>
              <div>
                <Label>Name</Label>
                <Input
                  value={tokenBName}
                  onChange={(e) => setTokenBName(e.target.value)}
                  placeholder="Gems"
                />
              </div>
              <div>
                <Label>Source</Label>
                <select
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  value={tokenBSource}
                  onChange={(e) => {
                    setTokenBSource(e.target.value as 'inventory' | 'external');
                    setTokenBInventory(null);
                  }}
                >
                  <option value="inventory">Inventory (DFE)</option>
                  <option value="external">External (Simulated)</option>
                </select>
              </div>
              {tokenBSource === 'inventory' && (
                <InventoryTokenPicker
                  label="Inventory Item"
                  selected={tokenBInventory}
                  onSelect={setTokenBInventory}
                />
              )}
              <div>
                <Label>Decimals</Label>
                <Input value={tokenBDecimals} onChange={(e) => setTokenBDecimals(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fee (basis points, 1-1000)</Label>
              <Input value={feeBps} onChange={(e) => setFeeBps(e.target.value)} placeholder="30" />
            </div>
            <div>
              <Label>Minimum Liquidity (first deposit lock)</Label>
              <Input
                value={minLiquidity}
                onChange={(e) => setMinLiquidity(e.target.value)}
                placeholder="1000"
              />
              <p className="text-xs text-muted-foreground mt-1">
                First depositor must provide sqrt(tokenA x tokenB) &gt; this value
              </p>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
          {success && <p className="text-sm text-green-600">{success}</p>}

          <Button
            onClick={handleCreate}
            disabled={!name || !tokenASymbol || !tokenBSymbol || submitting}
          >
            {submitting ? 'Creating...' : 'Create Cache'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
